import { useState, useEffect, useCallback, useRef } from 'react'
import { useSearchParams, useParams, useNavigate } from 'react-router-dom'
import { IconPlus, IconLayoutBottombar, IconDotsVertical, IconPencil, IconTrash, IconCopy, IconEye } from '@tabler/icons-react'
import { FullScreenModal } from '../../components/ui/FullScreenModal.tsx'
import { InlineError } from '../../components/ui/ErrorBoundary.tsx'
import { containersApi, type Container } from '../../lib/api.ts'
import { useToast } from '../../components/ui/Toast.tsx'

// ── Bin Form — new / edit / view ──────────────────────────────────────────────

type BinFormMode = 'new' | 'edit' | 'view'

function BinForm({ open, onClose, onSaved, lockers, allContainers, bin, mode: initMode = 'new', defaultParentId }: {
  open: boolean; onClose: () => void; onSaved: () => void
  lockers: Container[]
  allContainers: Container[]   // lockers + bins for parent picker
  bin?: Container | null
  mode?: BinFormMode
  defaultParentId?: string
}) {
  const { success, error } = useToast()
  const [mode, setMode] = useState<BinFormMode>(initMode)
  const isView = mode === 'view'
  const isNew = mode === 'new'

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [barcode, setBarcode] = useState('')
  const [parentId, setParentId] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setMode(initMode)
    if (bin) {
      setName(bin.name)
      setDescription(bin.description ?? '')
      setBarcode(bin.barcode ?? '')
      setParentId(bin.parentId ?? defaultParentId ?? lockers[0]?.id ?? '')
    } else {
      setName('')
      setDescription('')
      setBarcode('')
      setParentId(defaultParentId ?? lockers[0]?.id ?? '')
    }
  }, [open, bin?.id, initMode])

  const handleSave = async (keepOpen = false) => {
    if (!name.trim()) return
    setSaving(true)
    try {
      if (isNew) {
        await containersApi.create({
          name: name.trim(),
          description: description.trim() || null,
          barcode: barcode.trim() || null,
          parentId: parentId || null,
          typeName: 'bin',
        })
        success('Gear bag created', name)
      } else if (bin) {
        // Update name/description/barcode
        await containersApi.update(bin.id, {
          name: name.trim(),
          description: description.trim() || null,
          barcode: barcode.trim() || null,
        })
        // If parent changed, use the move endpoint (handles loop prevention server-side)
        if (parentId && parentId !== bin.parentId) {
          await containersApi.move(bin.id, parentId)
        }
        success('Gear bag updated', name)
      }
      onSaved()
      if (!keepOpen) onClose()
    } catch (e: any) {
      error(isNew ? 'Failed to create gear bag' : 'Failed to update gear bag', e.message)
    } finally { setSaving(false) }
  }

  const parentLabel = lockers.find(l => l.id === parentId)?.name ?? 'No locker selected'

  const footer = !isView ? (
    <>
      <button onClick={isNew ? onClose : () => setMode('view')}
        className="px-4 py-2 rounded-lg text-sm text-zinc-400 hover:text-zinc-200 hover:bg-surface-raised transition-colors">
        Cancel
      </button>
      <button onClick={() => handleSave(true)} disabled={saving || !name.trim()}
        className="px-4 py-2 rounded-lg text-sm border border-border-default text-zinc-300 hover:text-zinc-100 hover:bg-surface-raised disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
        {saving ? 'Saving...' : 'Save & Continue'}
      </button>
      <button onClick={() => handleSave(false)} disabled={saving || !name.trim()}
        className="px-5 py-2 rounded-lg text-sm font-medium bg-brand-orange text-white hover:bg-brand-orange-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
        {saving ? 'Saving...' : 'Save & Close'}
      </button>
    </>
  ) : undefined

  const headerActions = isView ? (
    <button onClick={() => setMode('edit')}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-border-default text-zinc-300 hover:text-zinc-100 hover:bg-surface-raised transition-colors">
      <IconPencil size={13} /> Edit
    </button>
  ) : undefined

  return (
    <FullScreenModal
      open={open} onClose={onClose} narrow
      title={name.trim() || (isNew ? 'New Gear Bag' : 'Gear Bag')}
      subtitle={isNew ? `Adding to ${parentLabel}` : isView ? 'Gear bag details' : 'Edit gear bag'}
      footer={footer}
      headerActions={headerActions}>
      <div className="px-5 py-5 space-y-4">
        {/* Locker / parent picker — always shown, read-only in view */}
        <div>
          <label className="block text-xs font-medium uppercase tracking-widest text-zinc-400 mb-2">
            Locker / Parent
          </label>
          {isView
            ? <p className="text-sm text-zinc-300">{allContainers.find(c => c.id === parentId)?.name ?? parentLabel}</p>
            : <select value={parentId} onChange={e => setParentId(e.target.value)}
                className="w-full px-3 py-2.5 bg-surface-raised border border-border-default rounded-lg text-sm text-zinc-200 outline-none focus:border-brand-orange/60 transition-colors">
                <option value="">No parent (top-level)</option>
                <optgroup label="Lockers">
                  {lockers.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </optgroup>
                {allContainers.filter(c => !c.isTopLevel && c.id !== bin?.id && c.parentId !== bin?.id).length > 0 && (
                  <optgroup label="Gear Bags">
                    {allContainers.filter(c => !c.isTopLevel && c.id !== bin?.id && c.parentId !== bin?.id).map(b => (
                      <option key={b.id} value={b.id}>
                        {lockers.find(l => l.id === b.parentId)?.name ? `${lockers.find(l => l.id === b.parentId)?.name} › ` : ''}{b.name}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>}
        </div>
        <div>
          <label className="block text-xs font-medium uppercase tracking-widest text-zinc-400 mb-2">Name</label>
          {isView
            ? <p className="text-sm text-zinc-300">{name || '—'}</p>
            : <input value={name} onChange={e => setName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSave(false)}
                placeholder="e.g. Hockey gear, Leather collection..."
                autoFocus
                className="w-full px-3 py-2.5 bg-surface-raised border border-border-default rounded-lg text-sm text-zinc-200 placeholder-zinc-500 outline-none focus:border-brand-orange/60 transition-colors" />}
        </div>
        <div>
          <label className="block text-xs font-medium uppercase tracking-widest text-zinc-400 mb-2">Description</label>
          {isView
            ? <p className="text-sm text-zinc-400">{description || '—'}</p>
            : <textarea value={description} onChange={e => setDescription(e.target.value)}
                placeholder="Optional description..." rows={3}
                className="w-full px-3 py-2.5 bg-surface-raised border border-border-default rounded-lg text-sm text-zinc-200 placeholder-zinc-500 outline-none focus:border-brand-orange/60 transition-colors resize-none" />}
        </div>
        <div>
          <label className="block text-xs font-medium uppercase tracking-widest text-zinc-400 mb-2">Label / Barcode</label>
          {isView
            ? <p className="text-sm text-zinc-400">{barcode || '—'}</p>
            : <input value={barcode} onChange={e => setBarcode(e.target.value)}
                placeholder="e.g. physical label, barcode, QR code value..."
                className="w-full px-3 py-2.5 bg-surface-raised border border-border-default rounded-lg text-sm text-zinc-200 placeholder-zinc-500 outline-none focus:border-brand-orange/60 transition-colors" />}
        </div>
      </div>
    </FullScreenModal>
  )
}

// ── Bins Page ─────────────────────────────────────────────────────────────────

export function BinsPage() {
  const { error, success } = useToast()
  const navigate = useNavigate()
  const { id: lockerId } = useParams<{ id: string }>()
  const [bins, setBins] = useState<Container[]>([])
  const [lockers, setLockers] = useState<Container[]>([])
  const [allContainers, setAllContainers] = useState<Container[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Form state
  const [formOpen, setFormOpen] = useState(false)
  const [formMode, setFormMode] = useState<BinFormMode>('new')
  const [selectedBin, setSelectedBin] = useState<Container | null>(null)
  const [defaultParentId, setDefaultParentId] = useState<string | undefined>()

  // Context menu state
  const [menuBin, setMenuBin] = useState<Container | null>(null)
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // Hover card state — wired when hover card is implemented
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [hoverItem, setHoverItem] = useState<null>(null)
  const [hoverRect, setHoverRect] = useState<DOMRect | null>(null)

  const dismissHover = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current)
    setHoverItem(null)
    setHoverRect(null)
  }

  useEffect(() => {
    if (!menuBin) return
    const h = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuBin(null); setMenuPos(null)
      }
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [menuBin])

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      // Load all containers for parent picker, filter displayed bins to this locker
      const allRes = await containersApi.list({})
      const all = allRes.data ?? []
      const loadedLockers = all.filter((c: Container) => c.isTopLevel)
      const loadedBins = all.filter((c: Container) => !c.isTopLevel)
      setLockers(loadedLockers)
      // Only show bins belonging to the locker in the URL
      // If 'default' is in the URL, redirect to the real default locker's ID
      if (lockerId === 'default') {
        const defaultLocker = loadedLockers.find((l: Container) => l.isDefault) ?? loadedLockers[0]
        if (defaultLocker) { navigate(`/lockers/${defaultLocker.id}/bins`, { replace: true }); return }
      }
      setBins(lockerId ? loadedBins.filter((b: Container) => b.parentId === lockerId) : loadedBins)
      setAllContainers(all)
      setDefaultParentId(lockerId ?? loadedLockers[0]?.id)
    } catch (e: any) {
      setLoadError(e.message ?? 'Failed to load gear bags')
      error('Failed to load gear bags', e.message)
    } finally { setLoading(false) }
  }, [lockerId])

  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => { load() }, [load])

  useEffect(() => {
    const editId = searchParams.get('edit')
    if (editId && bins.length > 0) {
      const bin = bins.find(b => b.id === editId)
      if (bin) {
        setSelectedBin(bin)
        setFormMode('edit')
        setFormOpen(true)
        setSearchParams({}, { replace: true })
      }
    }
  }, [searchParams, bins])

  const openNew = (parentId?: string) => {
    dismissHover()
    setSelectedBin(null)
    setDefaultParentId(parentId ?? lockers[0]?.id)
    setFormMode('new')
    setFormOpen(true)
    setMenuBin(null)
  }

  const openView = (bin: Container) => {
    dismissHover()
    setSelectedBin(bin)
    setFormMode('view')
    setFormOpen(true)
    setMenuBin(null)
  }

  const openEdit = (bin: Container) => {
    dismissHover()
    setSelectedBin(bin)
    setFormMode('edit')
    setFormOpen(true)
    setMenuBin(null)
  }

  const handleDuplicate = async (bin: Container) => {
    setMenuBin(null)
    try {
      await containersApi.create({
        name: `${bin.name} (Copy)`,
        description: bin.description || null,
        barcode: bin.barcode || null,
        parentId: bin.parentId || null,
        typeName: 'bin',
      })
      success('Duplicated', `${bin.name} (Copy) created`)
      load()
    } catch (e: any) { error('Failed to duplicate', e.message) }
  }

  const handleDelete = async (bin: Container) => {
    setMenuBin(null)
    try {
      await containersApi.delete(bin.id)
      setBins(prev => prev.filter(b => b.id !== bin.id))
      success('Deleted', bin.name)
    } catch (e: any) { error('Failed to delete gear bag', e.message) }
  }

  const menuActions = menuBin ? [
    { label: 'View', icon: <IconEye size={13} />, onClick: () => openView(menuBin) },
    { label: 'Edit', icon: <IconPencil size={13} />, onClick: () => openEdit(menuBin) },
    { label: 'Add Sub-Gear Bag Here', icon: <IconPlus size={13} />, onClick: () => openNew(menuBin.id) },
    { label: 'Duplicate', icon: <IconCopy size={13} />, onClick: () => handleDuplicate(menuBin) },
    { label: 'Delete', icon: <IconTrash size={13} />, onClick: () => handleDelete(menuBin), danger: true },
  ] : []

  const parentName = (bin: Container) => lockers.find(l => l.id === bin.parentId)?.name

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-6 py-4 border-b border-border-default">
        <h1 className="text-sm font-medium text-zinc-200 flex-1">
          Gear Bags <span className="ml-2 text-zinc-500 font-normal text-xs">· {bins.length} gear bags{lockers.find(l => l.id === lockerId) ? ` in ${lockers.find(l => l.id === lockerId)!.name}` : ''}</span>
        </h1>
        <button onClick={() => openNew()}
          className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium bg-brand-orange text-white hover:bg-brand-orange-dark transition-colors">
          <IconPlus size={14} /> Add Gear Bag
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loadError ? (
          <div className="p-6"><InlineError message={loadError} onRetry={load} /></div>
        ) : loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-sm text-zinc-500">Loading gear bags...</div>
          </div>
        ) : lockers.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-surface-raised border border-border-default flex items-center justify-center mx-auto mb-4">
                <IconLayoutBottombar size={24} className="text-zinc-600" />
              </div>
              <h3 className="text-sm font-medium text-zinc-400 mb-1">No locker found</h3>
              <p className="text-xs text-zinc-500 max-w-xs">Create a locker first before adding gear bags.</p>
            </div>
          </div>
        ) : bins.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-surface-raised border border-border-default flex items-center justify-center mx-auto mb-4">
                <IconLayoutBottombar size={24} className="text-zinc-600" />
              </div>
              <h3 className="text-sm font-medium text-zinc-400 mb-1">No gear bags yet</h3>
              <p className="text-xs text-zinc-500 mb-4 max-w-xs">Organise your locker with gear bags to group your gear.</p>
              <button onClick={() => openNew()}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-orange text-white text-xs font-medium hover:bg-brand-orange-dark transition-colors mx-auto">
                <IconPlus size={14} /> Create your first gear bag
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {/* Context menu */}
            {menuBin && (
              <div ref={menuRef}
                style={menuPos
                  ? { position: 'fixed', top: menuPos.y, left: menuPos.x, zIndex: 9999 }
                  : { position: 'fixed', zIndex: 9999 }}
                className="w-44 bg-zinc-900 border border-border-strong rounded-xl shadow-2xl py-1 overflow-hidden">
                {menuActions.map((a, i) => (
                  <button key={i} type="button" onClick={a.onClick}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors text-left ${(a as any).danger ? 'text-red-400 hover:bg-red-950/40' : 'text-zinc-300 hover:bg-surface-raised'}`}>
                    {a.icon}{a.label}
                  </button>
                ))}
              </div>
            )}

            {bins.map(bin => (
              <div key={bin.id}
                onClick={() => openView(bin)}
                onContextMenu={e => { e.preventDefault(); setMenuPos({ x: e.clientX, y: e.clientY }); setMenuBin(bin) }}
                className="relative p-4 rounded-xl bg-surface-overlay border border-border-default hover:border-border-strong transition-colors cursor-pointer">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-zinc-700 to-zinc-800 border border-border-default flex items-center justify-center mb-3">
                  <svg viewBox="0 0 40 40" className="w-6 h-6 opacity-60" fill="none">
                    <rect x="2" y="7" width="36" height="26" rx="3" stroke="white" strokeWidth="1.8"/>
                    <path d="M2 14h36" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                    <path d="M10 7V5a3 3 0 016 0v2" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </div>
                <div className="text-sm font-medium text-zinc-200 truncate">{bin.name}</div>
                {parentName(bin) && (
                  <div className="text-[10px] text-zinc-600 mt-0.5 truncate">{parentName(bin)}</div>
                )}
                {bin.description && (
                  <div className="text-xs text-zinc-500 mt-0.5 truncate">{bin.description}</div>
                )}
                {/* ... menu */}
                <button
                  onClick={e => { e.stopPropagation(); setMenuPos(null); setMenuBin(bin) }}
                  className="absolute top-2 right-2 p-1.5 rounded-md text-zinc-600 hover:text-zinc-300 hover:bg-surface-base transition-all">
                  <IconDotsVertical size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <BinForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={load}
        lockers={lockers}
        allContainers={allContainers}
        bin={selectedBin}
        mode={formMode}
        defaultParentId={defaultParentId} />
    </div>
  )
}
